import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressEsbRuntimeEsbRuntimeServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/automations_runtime',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/automations_runtime',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/automations_runtime',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

function resolveWixAutomationsEsbResolverV1EsbConfigResolverUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/esb-config-resolver',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/automations/v1/events/report',
        destPath: '/v1/events/report',
      },
      {
        srcPath: '/automations/v1/events/bulk-report',
        destPath: '/v1/events/bulk-report',
      },
      {
        srcPath: '/automations/v1/events/cancel',
        destPath: '/v1/events/cancel',
      },
      {
        srcPath: '/automations/v1/events/bulk-cancel',
        destPath: '/v1/events/bulk-cancel',
      },
    ],
    'platform.rise.ai': [
      {
        srcPath: '/workflows/v1/events/report',
        destPath: '/v1/events/report',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/automations/v1/events/bulk-cancel',
        destPath: '/v1/events/bulk-cancel',
      },
      {
        srcPath: '/automations/v1/events/bulk-report',
        destPath: '/v1/events/bulk-report',
      },
      {
        srcPath: '/automations/v1/events/report',
        destPath: '/v1/events/report',
      },
      {
        srcPath: '/automations/v1/events/cancel',
        destPath: '/v1/events/cancel',
      },
    ],
    _: [
      {
        srcPath: '/automations/v1/events/bulk-cancel',
        destPath: '/v1/events/bulk-cancel',
      },
      {
        srcPath: '/automations/v1/events/cancel',
        destPath: '/v1/events/cancel',
      },
      {
        srcPath: '/automations/v1/events/report',
        destPath: '/v1/events/report',
      },
      {
        srcPath: '/automations/v1/events/bulk-report',
        destPath: '/v1/events/bulk-report',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_automations_activations';

/**
 * Reports an event and activates site automations with the specified trigger key.
 *
 *
 * Only the app that created a trigger can report events for it.
 * This means other apps can't report events for your triggers,
 * and you can't report events for another app's triggers.
 *
 * If your app supports canceling events,
 * `externalEntityId` must be provided.
 * `externalEntityId` is required when calling [cancelEvent()](#cancel-event).
 */
export function reportEvent(payload: object): RequestOptionsFactory<any> {
  function __reportEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.v2.activation',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.esb.resolver.v1.EsbConfigResolver.ReportEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsEsbResolverV1EsbConfigResolverUrl({
        protoPath: '/v1/events/report',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __reportEvent;
}

/** Bulk reports events and activates site automations with the specified trigger key. */
export function bulkReportEvent(payload: object): RequestOptionsFactory<any> {
  function __bulkReportEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.v2.activation',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.esb.resolver.v1.EsbConfigResolver.BulkReportEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsEsbResolverV1EsbConfigResolverUrl({
        protoPath: '/v1/events/bulk-report',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkReportEvent;
}

/** Bulk cancels any remaining actions for a trigger and external entities. */
export function bulkCancelEvent(payload: object): RequestOptionsFactory<any> {
  function __bulkCancelEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.v2.activation',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.esb.resolver.v1.EsbConfigResolver.BulkCancelEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsEsbResolverV1EsbConfigResolverUrl({
        protoPath: '/v1/events/bulk-cancel',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkCancelEvent;
}

/**
 * Cancels any remaining actions for a trigger and external entity.
 *
 *
 * Events are not cancelable by default.
 * To make an event cancelable,
 * you must first pass an `externalEntityId`
 * and the applicable `triggerKey` to [reportEvent()](#report-event).
 * When you call cancelEvent() with the same `externalEntityId` and `triggerKey`,
 * the event is canceled,
 * as are all other events that share the `externalEntityId` and `triggerKey`.
 */
export function cancelEvent(payload: object): RequestOptionsFactory<any> {
  function __cancelEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.v2.activation',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.esb.resolver.v1.EsbConfigResolver.CancelEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsEsbResolverV1EsbConfigResolverUrl({
        protoPath: '/v1/events/cancel',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __cancelEvent;
}

/** Rerun an automation using a previous activation's payload. */
export function rerunActivation(payload: object): RequestOptionsFactory<any> {
  function __rerunActivation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.v2.activation',
      method: 'POST' as any,
      methodFqn:
        'wix.automations.esb.resolver.v1.EsbConfigResolver.RerunActivation',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAutomationsEsbResolverV1EsbConfigResolverUrl({
        protoPath: '/v1/events/rerun-activation',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __rerunActivation;
}

/**
 * > **Note**: Call this method when using the [Action Provider service plugin](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/introduction) to run an action with a timeout.
 *
 * Reports to Wix that your action has completed.
 *
 * When Wix calls [Invoke](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/invoke) to run your [action that has a timeout](https://dev.wix.com/docs/api-reference/business-management/automations/actions/action-provider-service-plugin/introduction#action-timeouts), it pauses the automation until you call this method or until the timeout period ends. If your action has an [output schema](https://dev.wix.com/docs/api-reference/business-management/automations/actions/about-actions#the-output-schema), include your action's results.
 *
 * If you don't call this method in the timeout period, Wix [marks the action as failed](https://support.wix.com/en/article/wix-automations-understanding-run-logs#understanding-statuses) and continues to the next step in the automation. Subsequent actions may fail if they rely on your action's output.
 */
export function reportActionInvocationCompleted(
  payload: object
): RequestOptionsFactory<any> {
  function __reportActionInvocationCompleted({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.automations.v2.activation',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.esb.runtime.EsbRuntimeService.ReportActionInvocationCompleted',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressEsbRuntimeEsbRuntimeServiceUrl({
        protoPath: '/v1/report-action-invocation-completed',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __reportActionInvocationCompleted;
}
